import React, { useEffect, useMemo, useState } from 'react'
import {
  MailboxNav,
  MAILBOX_NAV_EMAIL_GROUPS,
  MAILBOX_NAV_EMAIL_SECTIONS,
  MAILBOX_NAV_FAX_GROUPS,
  MAILBOX_NAV_FAX_SECTIONS,
  MAILBOX_NAV_FOLDER_MENU,
  MAILBOX_NAV_LABEL_MENU,
  MAILBOX_NAV_PREVIEW_STATE_LABELS,
  MAILBOX_NAV_PREVIEW_STATES,
  type MailboxNavCreatePayload,
  type MailboxNavItem,
  type MailboxNavPreviewState,
  type MailboxNavSection,
  type MailboxNavUpdatePayload,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './MailboxNavShowcase.css'

function cloneSections(sections: MailboxNavSection[]): MailboxNavSection[] {
  return sections.map((section) => ({
    ...section,
    items: section.items.map((item) => ({ ...item })),
  }))
}

const MailboxNavShowcase: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false)
  const [selectedId, setSelectedId] = useState('inbox')
  const [sample, setSample] = useState<'fax' | 'email'>('fax')
  const [lastAction, setLastAction] = useState(
    'Select a mailbox, or create / edit Folders and Labels.',
  )
  const [highlightedState, setHighlightedState] =
    useState<MailboxNavPreviewState>('default')
  const [liveSections, setLiveSections] = useState(() =>
    cloneSections(MAILBOX_NAV_FAX_SECTIONS),
  )

  useEffect(() => {
    setLiveSections(
      cloneSections(
        sample === 'fax'
          ? MAILBOX_NAV_FAX_SECTIONS
          : MAILBOX_NAV_EMAIL_SECTIONS,
      ),
    )
  }, [sample])

  const liveGroups =
    sample === 'fax' ? MAILBOX_NAV_FAX_GROUPS : MAILBOX_NAV_EMAIL_GROUPS

  const handleCreate = (payload: MailboxNavCreatePayload): void => {
    setLiveSections((prev) =>
      prev.map((section) => {
        if (section.id !== payload.sectionId) {
          return section
        }

        const isLabel = section.kind === 'label'
        const newItem: MailboxNavItem = {
          id: `${section.id}-${Date.now()}`,
          label: payload.name,
          icon: section.itemIcon,
          color: isLabel ? payload.color : undefined,
          menuItems: isLabel ? MAILBOX_NAV_LABEL_MENU : MAILBOX_NAV_FOLDER_MENU,
        }

        return { ...section, items: [newItem, ...section.items] }
      }),
    )
    setLastAction(
      `Created "${payload.name}" in ${payload.sectionId}${
        payload.color ? ` (${payload.color})` : ''
      }.`,
    )
  }

  const handleUpdate = (payload: MailboxNavUpdatePayload): void => {
    setLiveSections((prev) =>
      prev.map((section) => {
        if (section.id !== payload.sectionId) {
          return section
        }

        return {
          ...section,
          items: section.items.map((item) =>
            item.id === payload.itemId
              ? {
                  ...item,
                  label: payload.name,
                  color: section.kind === 'label' ? payload.color : item.color,
                }
              : item,
          ),
        }
      }),
    )
    setLastAction(`Updated "${payload.name}" (${payload.itemId}).`)
  }

  const handleDelete = (sectionId: string, itemId: string): void => {
    setLiveSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) {
          return section
        }
        return {
          ...section,
          items: section.items.filter((item) => item.id !== itemId),
        }
      }),
    )
    setLastAction(`Deleted "${itemId}" from ${sectionId}.`)
  }

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'mailbox-nav-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="mailbox-nav-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as MailboxNavPreviewState,
                  )
                }
              >
                {MAILBOX_NAV_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {MAILBOX_NAV_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'mailbox-nav-sample',
            label: 'Live sample',
            control: (
              <select
                id="mailbox-nav-sample"
                className="ui-field-control"
                value={sample}
                onChange={(event) => {
                  const next = event.target.value as 'fax' | 'email'
                  setSample(next)
                  setSelectedId('inbox')
                  setLastAction(
                    next === 'fax'
                      ? 'Fax sample loaded — Inbox selected.'
                      : 'Email sample loaded — Inbox selected.',
                  )
                }}
              >
                <option value="fax">Fax</option>
                <option value="email">Email</option>
              </select>
            ),
          },
          {
            id: 'mailbox-nav-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="mailbox-nav-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, highlightedState, sample],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Mailbox nav"
        description="Message Center left rail — system mailboxes, Folders / Labels with create, edit, delete, counts, and color swatches (Fax & Email)."
        stageClassName="ui-preview__stage--wide mailbox-nav-showcase__stage-wrap"
      >
        <div
          className={`mailbox-nav-showcase__sheet${
            darkMode ? ' mailbox-nav-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Use + to create folders/labels, ⋯ to rename/edit or delete.
              Highlighted state Default uses these live controls.
            </p>
            <div className="mailbox-nav-showcase__live-layout">
              {highlightedState === 'default' ? (
                <MailboxNav
                  groups={liveGroups}
                  sections={liveSections}
                  value={selectedId}
                  onChange={(id) => {
                    setSelectedId(id)
                    setLastAction(`Selected "${id}".`)
                  }}
                  onCreate={handleCreate}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                  darkMode={darkMode}
                  width={260}
                />
              ) : (
                <MailboxNav
                  previewState={highlightedState}
                  darkMode={darkMode}
                  width={260}
                />
              )}
              <div
                className={`mailbox-nav-showcase__live-panel${
                  darkMode ? ' mailbox-nav-showcase__live-panel--dark' : ''
                }`}
              >
                <p className="mailbox-nav-showcase__panel-title">
                  {sample === 'fax' ? 'Fax' : 'Email'} — main column
                </p>
                <p className="mailbox-nav-showcase__panel-note">{lastAction}</p>
              </div>
            </div>
          </section>

          <section className="mailbox-nav-showcase__demo">
            <h3 className="ui-showcase-live__title">Fax sample</h3>
            <p className="ui-showcase-live__note">
              Static reference (create/edit enabled in Live preview).
            </p>
            <MailboxNav
              groups={MAILBOX_NAV_FAX_GROUPS}
              sections={MAILBOX_NAV_FAX_SECTIONS}
              defaultValue="inbox"
              darkMode={darkMode}
              width={260}
            />
          </section>

          <section className="mailbox-nav-showcase__demo">
            <h3 className="ui-showcase-live__title">Email sample</h3>
            <p className="ui-showcase-live__note">
              Static reference (create/edit enabled in Live preview).
            </p>
            <MailboxNav
              groups={MAILBOX_NAV_EMAIL_GROUPS}
              sections={MAILBOX_NAV_EMAIL_SECTIONS}
              defaultValue="inbox"
              darkMode={darkMode}
              width={260}
            />
          </section>

          <section className="mailbox-nav-showcase__sample">
            <h3 className="ui-showcase-live__title">Selected state</h3>
            <p className="ui-showcase-live__note">
              Shows the Highlighted state from the properties panel.
            </p>
            <div
              className={`mailbox-nav-showcase__row${
                darkMode ? ' mailbox-nav-showcase__row--dark' : ''
              }`}
            >
              <h4 className="mailbox-nav-showcase__label">
                {MAILBOX_NAV_PREVIEW_STATE_LABELS[highlightedState]}
              </h4>
              <MailboxNav
                previewState={highlightedState}
                darkMode={darkMode}
                width={260}
              />
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="MailboxNav"
        sections={propertySections}
      />
    </>
  )
}

export default MailboxNavShowcase
